/**
 * features/auth/hooks/useStorePickerStores.ts
 *
 * Fuente AUTORITATIVA del selector operativo, jerárquica (RPC
 * get_store_picker_stores, 00124):
 *   - owner            → todas las tiendas activas
 *   - admin / director → tiendas activas de sus empresas (alcance)
 *   - operativo        → tiendas por user_store_access (+ home)
 *
 * Reemplaza el filtrado por accessibleStores (que era correcto solo para
 * operativos). No se usa para POS/caja directamente: solo arma el picker.
 */

import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@lib/supabase/client'
import { marcarServidorAlcanzable } from '@lib/net/reachability'
import { useCajeroStore } from './useAuth'

/**
 * Última lista que devolvió el servidor, por usuario. Se usa SÓLO cuando el servidor no
 * contesta: sin esto, una caída de red se leía como "no tienes tiendas asignadas" y dejaba
 * al responsable fuera del POS. No concede alcance: repite el que ya concedió el servidor.
 */
const CLAVE_CACHE = 'celmaxx-picker-stores-v1'

function leerCache(uid: string): PickerStore[] | null {
  try {
    const crudo = localStorage.getItem(CLAVE_CACHE)
    if (!crudo) return null
    const guardado = JSON.parse(crudo) as { uid: string; stores: PickerStore[] }
    return guardado.uid === uid && Array.isArray(guardado.stores) ? guardado.stores : null
  } catch {
    return null
  }
}

function guardarCache(uid: string, stores: PickerStore[]): void {
  try {
    localStorage.setItem(CLAVE_CACHE, JSON.stringify({ uid, stores }))
  } catch {
    /* cuota llena o almacenamiento bloqueado: la caché es una comodidad, no un requisito */
  }
}

export interface PickerStore {
  store_id: string
  store_code: string
  store_name: string
  business_id: string
  business_brand: string
  business_name: string
}

export function useStorePickerStores() {
  const [stores, setStores] = useState<PickerStore[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    const uid = useCajeroStore.getState().cajero?.id ?? ''
    const { data, error: e } = await supabase.rpc('get_store_picker_stores')
    if (e) {
      // Sin código de PostgREST no hubo respuesta del API: no es "no tienes tiendas", es
      // que no se pudo preguntar. Se reutiliza la última respuesta para este usuario.
      if (!e.code) {
        marcarServidorAlcanzable(false)
        const guardadas = uid ? leerCache(uid) : null
        if (guardadas) {
          setStores(guardadas)
          setLoading(false)
          return
        }
      }
      setError(e.message)
      setLoading(false)
      return
    }
    const lista = (data ?? []) as PickerStore[]
    setStores(lista)
    if (uid) guardarCache(uid, lista)
    setLoading(false)
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  return { stores, loading, error, refresh: load }
}
